import { useState, useCallback } from "react";

export type CropOptions = {
  src?: string;
  file?: File;
  x: number;
  y: number;
  width: number;
  height: number;
  targetWidth?: number;
  targetHeight?: number;
  filename?: string;
};

const readFileAsDataURL = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      resolve(reader.result as string);
    });
    reader.addEventListener("error", reject);
    reader.readAsDataURL(file);
  });

export async function cropToFile({
  src,
  file,
  x,
  y,
  width,
  height,
  targetWidth,
  targetHeight,
  filename,
}: CropOptions): Promise<File> {
  if (!src && file) {
    src = await readFileAsDataURL(file);
  }

  if (!src) {
    throw "Source image not provided";
  }

  const resolvedSrc = src;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject("Canvas context not available");
        return;
      }

      // Se não forem passados novos tamanhos, mantém o original
      const finalWidth = targetWidth || width;
      const finalHeight = targetHeight || height;

      canvas.width = finalWidth;
      canvas.height = finalHeight;

      ctx.drawImage(img, x, y, width, height, 0, 0, finalWidth, finalHeight);

      // Detecta o formato pelo src
      let mime = "image/png";
      if (resolvedSrc.match(/\.(jpe?g)$/i) || resolvedSrc.startsWith("data:image/jpeg")) {
        mime = "image/jpeg";
      }

      canvas.toBlob(blob => {
        if (!blob) {
          reject("Erro ao gerar blob da imagem");
          return;
        }

        const file = new File([blob], filename || `cropped.${mime === "image/png" ? "png" : "jpg"}`, { type: mime });
        resolve(file);
      }, mime);
    };

    img.onerror = reject;
    img.src = resolvedSrc;
  });
}

export function useCropImage() {
  const [croppedFile, setCroppedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const crop = useCallback(async (options: CropOptions) => {
    try {
      const file = await cropToFile(options);
      setCroppedFile(file);

      // Cria um preview temporário
      const url = URL.createObjectURL(file);
      setPreview(url);

      return file;
    } catch (err) {
      console.error("Erro ao cortar imagem:", err);
      throw err;
    }
  }, []);

  // opcional: liberar o objeto quando não precisar mais
  const clear = useCallback(() => {
    if (preview) URL.revokeObjectURL(preview);
    setCroppedFile(null);
    setPreview(null);
  }, [preview]);

  return { croppedFile, preview, crop, clear };
}
